// @flow
import type { Map } from 'leaflet';
import { get, isEqual } from 'lodash';
import { numberPointsCallbacksFiber } from 'esm-web-pro/containers/pages/network/objects/fiber/MapFiber';
import type { VisualisedFiber } from 'esm-web-pro/containers/pages/network/objects/cable/FiberList';
import { cleanUpNumberedPointIcon, numberedPointIcon } from 'esm-web-pro/containers/pages/network/Util';
import L from 'leaflet';

// The key of the object is the id of the fiber from the circuit and the array the coordinates of its path
type CircuitPaths = {
    [number | string]: Array<[number, number]>
}

const highlightCircuit = (circuitPaths: CircuitPaths, highlightFiberIds: Array<number>,
    map: Map, getFiberStyleCallback: Function, animationStartCoordinates?: [number, number]) => {
    const circuitPath = [];
    const fibersCallbacks = [];
    Object.keys(circuitPaths).forEach((key) => {
        let fiberPath = circuitPaths[key].map((c) => [c[1], c[0]]);
        if (animationStartCoordinates && !isEqual(animationStartCoordinates, fiberPath[0])) {
            fiberPath = fiberPath.reverse();
        }

        const isHighlighted = highlightFiberIds.includes(Number(key));
        const fiberCallback = getFiberStyleCallback(fiberPath, isHighlighted, map);
        if (isHighlighted) {
            fibersCallbacks.push(fiberCallback);
        } else {
            fibersCallbacks.unshift(fiberCallback);
        }

        circuitPath.push(...fiberPath);
    });

    return [fibersCallbacks, circuitPath];
};

const highlightFiberCircuit = (circuitPaths: CircuitPaths, fiber: VisualisedFiber, map: Map,
    getFiberStyleCallback: Function, animationStartCoordinates?: [number, number]) => {
    const [fibersCallbacks, circuitPath] = highlightCircuit(circuitPaths, [fiber.id], map,
        getFiberStyleCallback, animationStartCoordinates);
    const numberedPointsCallback = numberPointsCallbacksFiber(fiber);

    return [numberedPointsCallback.concat(fibersCallbacks), circuitPath];
};


const pointsHighlightCallback = (points: Array<Object>, callback: Function, pointsNumbers: Array<number>) => {
    const allCoordinates = points.map((point) => point.getLatLng());
    const layers = [];

    return {
        run: (layer) => {
            if (get(layer, 'feature.geometry.type') !== 'Point') return;

            allCoordinates.forEach((coordinates, index) => {
                if (layer.getLatLng().equals(coordinates)) {
                    const pointNumber = pointsNumbers ? pointsNumbers[index] : index + 1;
                    layer.setIcon(numberedPointIcon(layer, pointNumber));
                    if (callback) {
                        layer.off('click', callback);
                    }
                    layers.push(layer);
                }
            });
        },
        cleanUp: () => {
            layers.forEach((layer) => layer.setIcon(cleanUpNumberedPointIcon(layer)));
        },
    };
};

const pointsCoordinatesHighlightCallback = (coordinates: Array<[number, number]>, callback: Function,
    pointsNumbers: Array<number>) => {
    const layers = [];

    return {
        run: (layer) => {
            if (get(layer, 'feature.geometry.type') !== 'Point') return;

            coordinates.forEach((coordinate, index) => {
                const latLng = L.latLng(coordinate[0], coordinate[1]);

                if (layer.getLatLng().equals(latLng)) {
                    const pointNumber = (pointsNumbers && pointsNumbers[index]) ? pointsNumbers[index] : index;
                    layer.setIcon(numberedPointIcon(layer, pointNumber));
                    if (callback) {
                        layer.off('click', callback);
                    }
                    layers.push(layer);
                }
            });
        },
        cleanUp: () => {
            layers.forEach((layer) => layer.setIcon(cleanUpNumberedPointIcon(layer)));
        },
    };
};

// TODO
const highlightSpliceCircuit = () => {};

export {
    highlightFiberCircuit, highlightSpliceCircuit, pointsHighlightCallback, pointsCoordinatesHighlightCallback,
};
